import { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { useNotification } from "@context/NotificationContext";
import { extractErrorMessage } from "@api/axios";
import * as statisticsApi from "@api/statistics.api";
import type { PlayerStat } from "@api/statistics.api";
import { DataGridSurface } from "@components/DataGridSurface";
import { LoadingBox } from "@components/LoadingBox";
import { PAGE_CONTAINER_SX, dataGridFooterRadiusSx } from "@/constants/layout";

export default function Statistics() {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const [stats, setStats] = useState<PlayerStat[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await statisticsApi.getPlayerStats();
        setStats(data);
      } catch (err) {
        notify(extractErrorMessage(err), "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [notify]);

  const columns: GridColDef[] = useMemo(
    () => [
      { field: "name", headerName: t("players.name"), flex: 1, minWidth: 130 },
      {
        field: "attendance",
        headerName: t("statistics.played"),
        width: 80,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "wins",
        headerName: t("statistics.wins"),
        width: 70,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "draws",
        headerName: t("statistics.draws"),
        width: 70,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "losses",
        headerName: t("statistics.losses"),
        width: 70,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "goals",
        headerName: t("statistics.goals"),
        width: 70,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "assists",
        headerName: t("statistics.assists"),
        width: 80,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "ownGoals",
        headerName: t("statistics.ownGoals"),
        width: 80,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "bonusPoints",
        headerName: t("players.bonus"),
        width: 80,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "efficiency",
        headerName: t("statistics.efficiency"),
        width: 100,
        align: "center",
        headerAlign: "center",
      },
    ],
    [t],
  );

  if (loading) {
    return <LoadingBox />;
  }

  return (
    <Box sx={PAGE_CONTAINER_SX}>
      <Typography
        variant="h5"
        gutterBottom
        sx={{
          fontWeight: 600,
        }}
      >
        {t("statistics.title")}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          mb: 3,
        }}
      >
        {t("statistics.subtitle")}
      </Typography>
      <DataGridSurface>
        <DataGrid
          rows={stats}
          columns={columns}
          autoHeight
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
            sorting: { sortModel: [{ field: "efficiency", sort: "desc" }] },
          }}
          sx={dataGridFooterRadiusSx}
        />
      </DataGridSurface>
    </Box>
  );
}
